/**
 * Tamkeen Engine — خوارزمية اختيار بطاقات التمكين
 *
 * النظام النشط: FSRS v4 (Free Spaced Repetition Scheduler)
 * الصيغة: R(t, S) = (1 + t / (9 × S))^(-1)
 * المعاملات: القيم الافتراضية العالمية المُعايَرة على مئات ملايين المراجعات
 * المرجع: Ye et al. 2022-2024 — https://github.com/open-spaced-repetition/fsrs4anki
 *
 * التعديل الوحيد: نظام ثنائي (صح/خطأ) بدلاً من 4 أزرار
 *   صح → FSRS "Good" (G=3)
 *   خطأ → FSRS "Again" (G=1)
 *   الصعوبة D ثابتة = 5 (متوسطة) لعدم تتبع صعوبة كل بطاقة
 */

// ═══════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════

interface TimestampLike {
  toDate?: () => Date;
  seconds?: number;
  toMillis?: () => number;
}

export interface CardStateLike {
  c?: number;
  w?: number;
  s?: number;
  h?: number;
  di?: number;
  d?: string;
  f?: string;
  t?: TimestampLike | number | null;
}

export interface ScoreResult {
  recall: number;
  reasons: string[];
  debug: string;
}

export interface MasteryResult {
  score: number;
  studied: number;
  declining: number;
}

export interface TamkeenCard {
  id: string;
  pairId?: string;
  [key: string]: unknown;
}

export interface SelectedCard extends TamkeenCard {
  weaknessReasons: string[];
  weaknessDebug: string;
}

// ═══════════════════════════════════════════════════════════════
//  FSRS v4 — Free Spaced Repetition Scheduler
//  R = (1 + Δ / (9 × S))^(-1)
//  R = احتمالية التذكر (Retrievability)
//  Δ = الأيام منذ آخر مراجعة
//  S = الثبات (Stability) — عند Δ = S، احتمالية التذكر ≈ 90%
// ═══════════════════════════════════════════════════════════════

// FSRS v4 default parameters — trained on hundreds of millions of reviews
// Source: https://github.com/open-spaced-repetition/fsrs4anki/wiki/The-Algorithm
const FSRS_W: readonly number[] = [0.4, 0.6, 2.4, 5.8, 4.93, 0.94, 0.86, 0.01, 1.49, 0.14, 0.94, 2.18, 0.05, 0.34, 1.26, 0.29, 2.61];
const FSRS_D = 5;
const DEFAULT_FACTOR = 9;

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_HALF_LIFE = 365;
const MIN_HALF_LIFE = 0.4;

export function retentionToFactor(r: number | null | undefined): number {
  if (!r || r <= 0 || r >= 1) return DEFAULT_FACTOR;
  return r / (1 - r);
}

function tsToMs(ts: TimestampLike | number | null | undefined): number | null {
  if (!ts) return null;
  if (typeof ts === 'object' && 'toDate' in ts && typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (typeof ts === 'number') return ts;
  return null;
}

function recallProbability(deltaDays: number, stability: number, factor: number = DEFAULT_FACTOR): number {
  if (deltaDays <= 0) return 1;
  if (stability <= 0) return 0;
  return Math.pow(1 + deltaDays / (factor * stability), -1);
}

/**
 * D₀(G) = w4 - (G-3) × w5        (initial)
 * D'(D,G) = w7×D₀(3) + (1-w7)×(D - w6×(G-3))  (update with mean reversion)
 * Clamped to [1, 10].
 */
export function computeNewDifficulty(currentD: number | null | undefined, isCorrect: boolean): number {
  const G = isCorrect ? 3 : 1;
  if (currentD == null) {
    return Math.min(Math.max(FSRS_W[4] - (G - 3) * FSRS_W[5], 1), 10);
  }
  const D0_3 = FSRS_W[4];
  const newD = FSRS_W[7] * D0_3 + (1 - FSRS_W[7]) * (currentD - FSRS_W[6] * (G - 3));
  return Math.min(Math.max(newD, 1), 10);
}

export function computeNewHalfLife(
  currentH: number | null | undefined,
  isCorrect: boolean,
  deltaDays: number | null = null,
  difficulty: number = FSRS_D,
): number {
  const D = Math.min(Math.max(difficulty, 1), 10);

  if (!currentH || currentH <= 0) {
    return isCorrect ? FSRS_W[2] : FSRS_W[0];
  }

  const S = currentH;
  const R = (deltaDays != null && deltaDays > 0)
    ? recallProbability(deltaDays, S)
    : 0.9;

  if (isCorrect) {
    const SInc = Math.exp(FSRS_W[8]) * (11 - D) * Math.pow(S, -FSRS_W[9])
      * (Math.exp(FSRS_W[10] * (1 - R)) - 1) + 1;
    return Math.min(S * Math.max(SInc, 1), MAX_HALF_LIFE);
  }

  const newS = FSRS_W[11] * Math.pow(D, -FSRS_W[12])
    * (Math.pow(S + 1, FSRS_W[13]) - 1)
    * Math.exp(FSRS_W[14] * (1 - R));
  return Math.max(Math.min(newS, S), MIN_HALF_LIFE);
}

function estimateHalfLife(state: CardStateLike): number {
  if (state.h != null && state.h > 0) return state.h;
  const c = state.c || 0;
  const w = state.w || 0;
  const total = c + w;
  if (total === 0) return FSRS_W[2];
  const accuracy = c / total;
  const streak = state.s || 0;
  if (streak === 0) return FSRS_W[0];
  if (accuracy < 0.5) return FSRS_W[1];
  if (accuracy <= 0.7) return FSRS_W[2];
  if (streak >= 3 && accuracy > 0.9) return FSRS_W[3];
  return FSRS_W[2];
}

export function scoreCard(state: CardStateLike | null | undefined, factor: number = DEFAULT_FACTOR): ScoreResult {
  if (!state) {
    return { recall: 0, reasons: ['لم تدرسها بعد'], debug: 'بطاقة جديدة — لا توجد بيانات سابقة' };
  }

  const now = Date.now();
  const lastMs = tsToMs(state.t);
  const hFromState = !!state.h;
  const h = estimateHalfLife(state);

  if (!lastMs) {
    return { recall: 0, reasons: ['لم تدرسها بعد'], debug: 'لا يوجد وقت مراجعة سابق' };
  }

  const deltaDays = (now - lastMs) / DAY_MS;
  const p = recallProbability(deltaDays, h, factor);
  const pct = Math.round(p * 100);

  const c = state.c || 0;
  const w = state.w || 0;
  const total = c + w;
  const streak = state.s || 0;
  const accuracy = total > 0 ? c / total : 0;
  const accPct = Math.round(accuracy * 100);

  const reasons: string[] = [];
  if (p < 0.3) {
    reasons.push(`احتمالية تذكرك ${pct}% — تحتاج مراجعة عاجلة`);
  } else if (p < 0.5) {
    reasons.push(`احتمالية تذكرك ${pct}% — بدأت تنساها`);
  } else if (p < 0.7) {
    reasons.push(`احتمالية تذكرك ${pct}% — تحتاج تثبيت`);
  } else if (p < 0.85) {
    reasons.push(`احتمالية تذكرك ${pct}%`);
  }

  if (total > 0) {
    if (accuracy < 0.5) {
      reasons.push(`دقتك فيها ${accPct}% فقط`);
    } else if (accuracy <= 0.7) {
      reasons.push(`دقتك فيها ${accPct}%`);
    }
  }
  if (streak === 0 && w > 0) {
    reasons.push('آخر إجابة كانت خطأ');
  }

  if (reasons.length === 0) {
    if (p >= 0.85) {
      reasons.push(`احتمالية تذكرك ${pct}% — مراجعة للتثبيت`);
    } else {
      reasons.push(`احتمالية تذكرك ${pct}%`);
    }
  }

  let deltaLabel: string;
  if (deltaDays < 1 / 24) {
    deltaLabel = `${Math.round(deltaDays * 24 * 60)}min`;
  } else if (deltaDays < 1) {
    deltaLabel = `${Math.round(deltaDays * 24)}h`;
  } else {
    deltaLabel = `${Math.round(deltaDays * 10) / 10}d`;
  }

  const debugHlr = `FSRS: S=${Math.round(h * 100) / 100}d (${hFromState ? 'stored' : 'estimated'}) · last=${deltaLabel} ago`;
  const debugInfo = `Info: c=${c} w=${w} streak=${streak} acc=${accPct}%`;

  return { recall: p, reasons, debug: debugHlr + '\n' + debugInfo };
}

export function computeMasteryScore(
  states: Record<string, CardStateLike> | null | undefined,
  factor: number = DEFAULT_FACTOR,
): MasteryResult {
  const entries = Object.values(states || {});
  let totalRecall = 0;
  let studied = 0;
  let declining = 0;

  for (const state of entries) {
    if (!state || !state.t) continue;
    const { recall } = scoreCard(state, factor);
    totalRecall += recall;
    studied++;
    if (recall < 0.7) declining++;
  }

  if (studied === 0) return { score: -1, studied: 0, declining: 0 };
  return {
    score: Math.round((totalRecall / studied) * 100),
    studied,
    declining,
  };
}

export function selectTamkeenCards(
  cards: TamkeenCard[],
  states: Record<string, CardStateLike>,
  target: number,
  factor: number = DEFAULT_FACTOR,
): SelectedCard[] {
  const evaluated = cards.map((card) => {
    const state = states[card.id];
    const { recall, reasons, debug } = scoreCard(state, factor);
    return { card, recall, reasons, debug };
  });

  evaluated.sort((a, b) => a.recall - b.recall);

  const usedPairs = new Set<string>();
  const deduped: typeof evaluated = [];
  for (const item of evaluated) {
    const pid = item.card.pairId;
    if (pid && usedPairs.has(pid)) continue;
    if (pid) usedPairs.add(pid);
    deduped.push(item);
  }

  const selected: SelectedCard[] = deduped.slice(0, target).map((item) => {
    const reasons = item.reasons.length > 0
      ? item.reasons
      : ['مراجعة عامة'];
    return { ...item.card, weaknessReasons: reasons, weaknessDebug: item.debug };
  });

  for (let i = selected.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [selected[i], selected[j]] = [selected[j], selected[i]];
  }

  return selected;
}
